/**
 * Custom hook for managing analytics dashboard preferences
 * Persists to localStorage: tab order, hidden cards, hidden tabs
 */

import { useState, useCallback } from "react";

const STORAGE_KEY = "analytics_preferences";

const DEFAULT_PREFERENCES = {
  summaryCardOrder: null, // null = use default from config
  hiddenSummaryCards: [],
  tabOrder: null, // null = use default from config  
  hiddenTabs: [],
  defaultRowCount: 10,
};

// Read from localStorage (only called once during initialization)
const getInitialPreferences = () => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return { ...DEFAULT_PREFERENCES, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error("Failed to load analytics preferences:", error);
  }
  return DEFAULT_PREFERENCES;
};

export const useAnalyticsPreferences = () => {
  // Use lazy initialization to read from localStorage only once
  const [preferences, setPreferences] = useState(getInitialPreferences);

  // Reorder summary cards
  const reorderSummaryCards = useCallback((newOrder) => {
    setPreferences(current => {
      const updated = { ...current, summaryCardOrder: newOrder };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error("Failed to save preferences:", e);
      }
      return updated;
    });
  }, []);

  // Toggle summary card visibility
  const toggleSummaryCard = useCallback((cardKey) => {
    setPreferences(current => {
      const hidden = current.hiddenSummaryCards.includes(cardKey)
        ? current.hiddenSummaryCards.filter(k => k !== cardKey)
        : [...current.hiddenSummaryCards, cardKey];
      const updated = { ...current, hiddenSummaryCards: hidden };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error("Failed to save preferences:", e);
      }
      return updated;
    });
  }, []);

  // Reorder tabs
  const reorderTabs = useCallback((newOrder) => {
    setPreferences(current => {
      const updated = { ...current, tabOrder: newOrder };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error("Failed to save preferences:", e);
      }
      return updated;
    });
  }, []);

  // Toggle tab visibility
  const toggleTab = useCallback((tabId) => {
    setPreferences(current => {
      const hidden = current.hiddenTabs.includes(tabId)
        ? current.hiddenTabs.filter(id => id !== tabId)
        : [...current.hiddenTabs, tabId];
      const updated = { ...current, hiddenTabs: hidden };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error("Failed to save preferences:", e);
      }
      return updated;
    });
  }, []);

  // Set default row count
  const setDefaultRowCount = useCallback((count) => {
    setPreferences(current => {
      const updated = { ...current, defaultRowCount: count };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
      } catch (e) {
        console.error("Failed to save preferences:", e);
      }
      return updated;
    });
  }, []);

  // Reset all preferences to defaults
  const resetPreferences = useCallback(() => {
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.error("Failed to remove preferences:", e);
    }
    setPreferences(DEFAULT_PREFERENCES);
  }, []);

  // Get ordered and filtered summary cards - stable function
  const getOrderedSummaryCards = useCallback((defaultCards) => {
    const { summaryCardOrder, hiddenSummaryCards } = preferences;
    let cards = [...defaultCards];
    
    if (summaryCardOrder) {
      cards = summaryCardOrder
        .map(key => defaultCards.find(c => c.key === key))
        .filter(Boolean);
      
      defaultCards.forEach(card => {
        if (!summaryCardOrder.includes(card.key)) {
          cards.push(card);
        }
      });
    }
    
    return cards.filter(c => !hiddenSummaryCards.includes(c.key));
  }, [preferences.summaryCardOrder, preferences.hiddenSummaryCards]);

  // Get ordered and filtered tabs - stable function
  const getOrderedTabs = useCallback((defaultTabs) => {
    const { tabOrder, hiddenTabs } = preferences;
    let tabs = [...defaultTabs];
    
    if (tabOrder) {
      tabs = tabOrder
        .map(id => defaultTabs.find(t => t.id === id))
        .filter(Boolean);
      
      defaultTabs.forEach(tab => {
        if (!tabOrder.includes(tab.id)) {
          tabs.push(tab);
        }
      });
    }
    
    return tabs.filter(t => !hiddenTabs.includes(t.id));
  }, [preferences.tabOrder, preferences.hiddenTabs]);

  return {
    preferences,
    loaded: true,
    reorderSummaryCards,
    toggleSummaryCard,
    reorderTabs,
    toggleTab,
    setDefaultRowCount,
    resetPreferences,
    getOrderedSummaryCards,
    getOrderedTabs,
  };
};

export default useAnalyticsPreferences;
